// API configuration
const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://localhost:3000/api/v1';

/**
 * Fetch planning activities from the API
 * 
 * @param {Object} params - Query parameters
 * @param {string|Date} [params.date] - Filter by specific date (ISO format or YYYY-MM-DD)
 * @param {string} [params.equipment_id] - Filter by specific equipment ID
 * 
 * @returns {Promise<{
 *   success: boolean,
 *   activities: Array<{
 *     id: number,
 *     equipment_id: string,
 *     equipment_name: string,
 *     activity_type: 'manufacturing' | 'cleaning' | 'maintenance',
 *     batch_number: string | null,
 *     cleaning_type: string | null,
 *     start_time: string,
 *     end_time: string
 *   }>,
 *   meta: {
 *     display_start_time: string,
 *     display_end_time: string,
 *     total_activities: number
 *   }
 * }>}
 */
export const fetchPlanning = async ({ date = null, equipment_id = null } = {}) => {
  try {
    // const url = new URL(`${API_BASE_URL}/equipment-planner/planning?date=2025-05-08`); 
    // Use current date if no date is provided
    const currentDate = date || new Date();
    const dateStr = currentDate instanceof Date ? currentDate.toISOString().split('T')[0] : currentDate;
    
    const url = new URL(`${API_BASE_URL}/equipment-planner/planning`);
    url.searchParams.append('date', dateStr);
    
    if (equipment_id) {
      url.searchParams.append('equipment_id', equipment_id);
    }

    const response = await fetch(url, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
      },
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const data = await response.json();
    
    if (!data.success) {
      throw new Error('API returned unsuccessful response');
    }
    
    return data;
  } catch (error) {
    console.error('Error fetching planning data:', error);
    throw error;
  }
};

/**
 * Fetch equipment list from the API
 * 
 * @returns {Promise<{
 *   success: boolean,
 *   data: Array<{
 *     id: string,
 *     equipment_name: string
 *   }>
 * }>}
 */
export const fetchEquipmentList = async () => {
  try {
    const response = await fetch(`${API_BASE_URL}/equipment-planner/equipment-list`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
      },
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const data = await response.json();
    
    if (!data.success) {
      throw new Error('API returned unsuccessful response');
    }
    
    return data;
  } catch (error) {
    console.error('Error fetching equipment list:', error);
    throw error;
  }
};

/**
 * Fetch equipment IDs from logs
 * 
 * @returns {Promise<{
 *   success: boolean,
 *   data: string[]
 * }>}
 */
export const fetchEquipmentFromLogs = async () => {
  try {
    const response = await fetch(`${API_BASE_URL}/equipment-planner/equipment-from-logs`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
      },
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const data = await response.json();
    
    if (!data.success) {
      throw new Error('API returned unsuccessful response');
    }
    
    return data;
  } catch (error) {
    console.error('Error fetching equipment from logs:', error);
    throw error;
  }
};

/**
 * Fetch activity details by ID
 * 
 * @param {number|string} activityId - The activity ID
 * 
 * @returns {Promise<{
 *   success: boolean,
 *   data: {
 *     id: number,
 *     eqp_log_type: string,
 *     equipment_id: string,
 *     product_batch: string,
 *     start_date: string,
 *     end_date: string
 *   }
 * }>}
 */
export const fetchActivityDetails = async (activityId) => {
  try {
    const response = await fetch(`${API_BASE_URL}/equipment-planner/activity/${activityId}`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
      },
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const data = await response.json();
    
    if (!data.success) {
      throw new Error('API returned unsuccessful response');
    }
    
    return data;
  } catch (error) {
    console.error('Error fetching activity details:', error);
    throw error;
  }
};

/**
 * Fetch equipment utilization summary
 * 
 * @param {Object} params - Query parameters
 * @param {string|Date} params.start_date - Start date (required)
 * @param {string|Date} params.end_date - End date (required)
 * @param {string} [params.equipment_id] - Optional equipment ID filter
 * 
 * @returns {Promise<{
 *   success: boolean,
 *   start_date: string,
 *   end_date: string,
 *   data: Array<{
 *     equipment_id: string,
 *     eqp_log_type: string,
 *     count: string,
 *     total_hours: string
 *   }>
 * }>}
 */
export const fetchUtilization = async ({ start_date, end_date, equipment_id = null }) => {
  try {
    const url = new URL(`${API_BASE_URL}/equipment-planner/utilization`);
    
    const startDateStr = start_date instanceof Date ? start_date.toISOString().split('T')[0] : start_date;
    const endDateStr = end_date instanceof Date ? end_date.toISOString().split('T')[0] : end_date;
    
    url.searchParams.append('start_date', startDateStr);
    url.searchParams.append('end_date', endDateStr);
    
    if (equipment_id) {
      url.searchParams.append('equipment_id', equipment_id);
    }

    const response = await fetch(url, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
      },
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const data = await response.json();
    
    if (!data.success) {
      throw new Error('API returned unsuccessful response');
    }
    
    return data;
  } catch (error) {
    console.error('Error fetching utilization data:', error);
    throw error;
  }
};

// Legacy exports for backward compatibility
export const fetchActivities = fetchPlanning;
export const fetchEquipment = fetchEquipmentList;
